import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeft, ArrowRight, BadgePercent, CalendarClock, Loader2, ShieldCheck, TicketPercent, Users, Wallet } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ErrorState, Loading } from '@/components/shared/feedback'
import { Button } from '@/components/ui/button'
import {
  useBillingMethods, useBillingPlans, useBillingSummary, useCheckout, useInvoiceProfiles, useQuote,
  type PaymentMethod, type PricingPlan, type QuotaKey, type Quotas,
} from '@/hooks/use-billing'
import { useAuthStore } from '@/stores/auth-store'
import { apiError } from '@/lib/api-client'
import { formatVnd } from '@/lib/format-money'
import { i18n } from '@/lib/i18n'
import { cn, formatNumber } from '@/lib/utils'
import {
  QUOTA_KEYS, QUOTA_META, addonDeltaText, daysLeft as daysLeftOf, isFreeOrder, isPromoBlocked, normalizePromoInput, resolvePlan, vatLabel, vatListNote,
} from './plans'
import { goToPayment, resultPath, safeFrom, writePendingOrder } from './payment-methods'
import { buildInvoiceBody, invoiceSummary, type InvoiceChoice, type InvoiceErrors } from './invoice-info'
import { InvoiceSection, initialChoice } from './invoice-section'
import { PaymentReturn } from './billing-page'
import { Fold, PayPalChargeNote, PaymentMethods, PromoCodeBox, pickMethod, QuotaGroup, QuotaStepper, Row, Section, VatRows } from './upgrade-page'

const LABEL: Record<QuotaKey, string> = {
  get members() { return i18n.t('billing:addon.quotaLabels.members') },
  get officialChannels() { return i18n.t('billing:addon.quotaLabels.officialChannels') },
  get personalChannels() { return i18n.t('billing:addon.quotaLabels.personalChannels') },
}

/** Số đang có hiệu lực của gói (số đã mua + mua thêm; không thấp hơn số gồm sẵn). */
function currentQuantities(plan: PricingPlan, purchased: Quotas | null): Quotas {
  const out = {} as Quotas
  for (const k of QUOTA_KEYS) out[k] = Math.max(plan.included[k], purchased?.[k] ?? 0)
  return out
}

/**
 * "Mua thêm" — tăng nhân viên / kênh cho PHẦN CÒN LẠI của kỳ đang dùng, không gia hạn.
 * Giá = phụ phí luỹ tiến của phần tăng × số ngày còn lại / 30, giữ % giảm theo thời hạn của đơn kỳ hiện tại
 * (server tính — POST /billing/quote { mode: 'addon' }, cùng hàm với checkout).
 */
export function AddonPage() {
  const { t } = useTranslation('billing')
  const [params, setParams] = useSearchParams()
  const summary = useBillingSummary()
  const catalog = useBillingPlans()
  const methods = useBillingMethods()
  const [chosenMethod, setChosenMethod] = useState<PaymentMethod | null>(null)
  const method = pickMethod(methods.data?.methods ?? [], chosenMethod)
  const role = useAuthStore((s) => s.user?.role)
  const userEmail = useAuthStore((s) => s.user?.email) ?? ''
  const canPay = role === 'owner' || role === 'admin'
  const profiles = useInvoiceProfiles(canPay)
  const [invoice, setInvoice] = useState<InvoiceChoice | null>(null)
  const [invoiceErrors, setInvoiceErrors] = useState<InvoiceErrors>({})
  useEffect(() => {
    if (invoice || !canPay || profiles.isLoading) return
    setInvoice(initialChoice(profiles.data, userEmail))
  }, [invoice, canPay, profiles.isLoading, profiles.data, userEmail])

  const s = summary.data
  const cfg = catalog.data
  const plan = cfg && s && !s.wasDowngraded ? cfg.plans.find((p) => p.id === s.effectivePlan) : undefined
  const active = !!s?.expiresAt && new Date(s.expiresAt).getTime() > Date.now()
  const eligible = !!plan && plan.purchasable && (plan.monthlyPrice ?? 0) > 0 && active
  const current = useMemo(() => (plan && s ? currentQuantities(plan, s.purchased) : null), [plan, s])

  const [qty, setQty] = useState<Quotas | null>(null)
  // Số đang có đổi (vừa mua thêm xong, summary tải lại) → điền lại.
  useEffect(() => { if (current) setQty(current) }, [current])

  const [promoCode, setPromoCode] = useState(() => normalizePromoInput(params.get('promo') ?? ''))
  const applyPromo = (code: string) => {
    setPromoCode(code)
    setParams((prev) => {
      const n = new URLSearchParams(prev)
      if (code) n.set('promo', code)
      else n.delete('promo')
      return n
    }, { replace: true })
  }

  const increased = !!qty && !!current && QUOTA_KEYS.some((k) => qty[k] > current[k])
  const quote = useQuote(eligible && qty && increased ? { mode: 'addon', quantities: qty, ...(promoCode ? { promoCode } : {}) } : null)

  // Tới từ popup "đã đạt hạn mức" (?quota=officialChannels…) — cuộn tới đúng mục, tô viền nổi bật.
  const quotaParam = params.get('quota')
  const highlightQuota = (QUOTA_KEYS as string[]).includes(quotaParam ?? '') ? (quotaParam as QuotaKey) : null
  useEffect(() => {
    if (!highlightQuota || summary.isLoading || catalog.isLoading) return
    document.getElementById(`quota-stepper-${highlightQuota}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [highlightQuota, summary.isLoading, catalog.isLoading])

  if (summary.isLoading || catalog.isLoading) return <Loading />
  if (summary.isError || !s || catalog.isError || !cfg) return <ErrorState message={t('addon.loadError')} />

  const currentPlan = resolvePlan(s.wasDowngraded ? 'free' : s.plan, cfg.plans)
  // B2: làm tròn XUỐNG, tối thiểu 1 — khớp số ngày server tính tiền.
  // Q3: làm tròn LÊN — cùng hàm với số ngày server tính tiền.
  const daysLeft = daysLeftOf(s.expiresAt)

  return (
    <div className="-m-4 flex min-h-full flex-col bg-card md:-m-6">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b px-5">
        <Link to="/billing" className="flex items-center gap-1.5 text-sm text-foreground hover:text-primary">
          <ArrowLeft className="h-4 w-4" /> {t('common:actions.back')}
        </Link>
        <span className="h-4 w-px bg-border" />
        <h1 className="text-base font-medium">{t('addon.pageTitle')}</h1>
      </header>

      <div className="px-5 pt-4 empty:hidden lg:px-6">
        <PaymentReturn onPaid={() => setPromoCode('')} />
      </div>

      {!eligible || !plan || !qty || !current ? (
        <div className="p-5 lg:p-6">
          <div className="max-w-xl space-y-3 rounded-xl border p-5 text-sm">
            <p className="font-medium">{t('addon.cannotAddonTitle')}</p>
            <p className="text-muted-foreground">
              {t('addon.cannotAddonDesc', { plan: currentPlan.name, expiredSuffix: s.expiresAt && !active ? t('addon.expiredSuffix') : '' })}
            </p>
            <Button asChild><Link to="/billing/upgrade">{t('addon.choosePlanOrRenew')}</Link></Button>
          </div>
        </div>
      ) : (
        <div className="grid flex-1 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px]">
          <div className="space-y-7 p-5 lg:p-6">
            <Section title={t('addon.currentPlanTitle')}>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border p-4 text-sm">
                <span className="font-medium">{plan.name}</span>
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <CalendarClock className="h-4 w-4" />
                  {t('addon.expiresOn', { date: new Date(s.expiresAt!).toLocaleDateString('vi-VN'), days: daysLeft })}
                </span>
                <span className="text-muted-foreground">
                  {t('addon.currentQuantities', { list: QUOTA_KEYS.map((k) => `${formatNumber(current[k])} ${QUOTA_META[k].short}`).join(' · ') })}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('addon.addonEffectNote')}{' '}
                <Link to={`/billing/upgrade?plan=${encodeURIComponent(plan.id)}`} className="text-primary hover:underline">{t('addon.renewOrChangeQuantity')}</Link>.
              </p>
            </Section>

            <Section title={t('addon.quantityAfterAddonTitle')}>
              <div className="grid grid-cols-1 gap-4 rounded-xl border p-4 md:grid-cols-[1fr_2fr]">
                <QuotaGroup title={t('addon.quotaLabels.members')} icon={Users}>
                  <QuotaStepper k="members" plan={plan} tiers={cfg.addons.members} qty={qty} setQty={setQty} used={s.usage.members}
                    floor={current.members} floorLabel={t('addon.floorLabel')} highlight={highlightQuota === 'members'} />
                </QuotaGroup>
                <QuotaGroup title={t('addon.socialAccountsTitle')} icon={Wallet}>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <QuotaStepper k="officialChannels" plan={plan} tiers={cfg.addons.officialChannels} qty={qty} setQty={setQty}
                      used={s.usage.channelsOfficial} floor={current.officialChannels} floorLabel={t('addon.floorLabel')} highlight={highlightQuota === 'officialChannels'} />
                    <QuotaStepper k="personalChannels" plan={plan} tiers={cfg.addons.personalChannels} qty={qty} setQty={setQty}
                      used={s.usage.channelsPersonal} floor={current.personalChannels} floorLabel={t('addon.floorLabel')} highlight={highlightQuota === 'personalChannels'} />
                  </div>
                </QuotaGroup>
              </div>
            </Section>

            <Section title={t('addon.paymentMethodTitle')}>
              <PaymentMethods methods={methods.data?.methods ?? []} loading={methods.isLoading} selected={method} onSelect={setChosenMethod} />
            </Section>

            {canPay && invoice && (
              <Section title={t('addon.invoiceInfoTitle')} id="invoice-info">
                <InvoiceSection
                notReady={methods.data?.invoiceReady === false}
                  choice={invoice}
                  onChange={(c) => { setInvoice(c); if (Object.keys(invoiceErrors).length) setInvoiceErrors({}) }}
                  profiles={profiles.data ?? []}
                  loadingProfiles={profiles.isLoading}
                  errors={invoiceErrors}
                  defaultEmail={userEmail}
                />
              </Section>
            )}
          </div>

          <AddonPreview
            current={current}
            qty={qty}
            increased={increased}
            quote={quote}
            canPay={canPay}
            method={method}
            promoCode={promoCode}
            onPromoChange={applyPromo}
            invoice={invoice}
            invoiceLabel={invoiceSummary(invoice, profiles.data ?? [])}
            onInvoiceErrors={setInvoiceErrors}
          />
        </div>
      )}
    </div>
  )
}

function AddonPreview({
  current, qty, increased, quote, canPay, method, promoCode, onPromoChange, invoice, invoiceLabel, onInvoiceErrors,
}: {
  current: Quotas
  qty: Quotas
  increased: boolean
  quote: ReturnType<typeof useQuote>
  canPay: boolean
  method: PaymentMethod | null
  promoCode: string
  onPromoChange: (code: string) => void
  invoice: InvoiceChoice | null
  invoiceLabel: string
  onInvoiceErrors: (e: InvoiceErrors) => void
}) {
  const { t } = useTranslation('billing')
  const checkout = useCheckout()
  const [redirecting, setRedirecting] = useState(false)
  // Báo giá cũ (placeholder) khi đã về "không tăng gì" → bỏ.
  const q = increased ? quote.data : undefined
  const busy = checkout.isPending || redirecting
  const stale = increased && (quote.isFetching || quote.isPlaceholderData)
  const freeOrder = isFreeOrder(q)
  const promoBlocked = isPromoBlocked(promoCode, q)

  const pay = () => {
    if (!q || (!method && !freeOrder) || promoBlocked) return
    const from = safeFrom(new URLSearchParams(window.location.search).get('from'))
    const inv = invoice && !freeOrder ? buildInvoiceBody(invoice) : { body: null, errors: {} }
    if (Object.keys(inv.errors).length) {
      onInvoiceErrors(inv.errors)
      document.getElementById('invoice-info')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      toast.error(t('addon.checkInvoiceInfo'))
      return
    }
    checkout.mutate(
      {
        mode: 'addon', quantities: qty, method: method ?? undefined, invoice: freeOrder ? null : inv.body,
        ...(q.promo ? { promoCode: q.promo.code } : {}),
        returnTo: resultPath(from),
      },
      {
        onSuccess: (r) => {
          if (r.method === 'credit') {
            toast.success(t('addon.toastPromoAddon'))
            window.location.assign(resultPath(from, { payment: 'return', order: r.orderId }))
            return
          }
          writePendingOrder(r.orderId, from)
          setRedirecting(true)
          try {
            goToPayment(r)
          } catch (err) {
            setRedirecting(false)
            writePendingOrder(null)
            toast.error(err instanceof Error ? err.message : t('orderActions.cannotOpenCheckout'))
          }
        },
        onError: (err) => toast.error(apiError(err)),
      },
    )
  }

  return (
    <aside className="border-t bg-muted/40 lg:border-l lg:border-t-0">
      <div className="space-y-4 p-5 lg:sticky lg:top-0 lg:p-6">
        <h2 className="flex items-center gap-2 text-base font-medium">
          <span className="h-4 w-1 rounded-full bg-primary" /> {t('addon.previewTitle')}
        </h2>

        <Fold title={t('addon.quantityFoldTitle')}>
          {QUOTA_KEYS.map((k) => {
            const up = qty[k] > current[k]
            return (
              <Row
                key={k}
                label={LABEL[k]}
                value={
                  <span className={cn('inline-flex items-center gap-1', up && 'font-medium text-primary')}>
                    {formatNumber(current[k])} <ArrowRight className="h-3 w-3" /> {formatNumber(qty[k])}
                    {up && <span className="text-xs">(+{qty[k] - current[k]})</span>}
                  </span>
                }
              />
            )
          })}
        </Fold>

        {!increased ? (
          <p className="text-sm text-muted-foreground">{t('addon.increaseToSeePrice')}</p>
        ) : q ? (
          <div className="space-y-1.5 text-sm">
            <Row label={t('addon.effectiveLabel')} value={`${new Date().toLocaleDateString('vi-VN')} → ${new Date(q.addon?.periodEnd ?? Date.now()).toLocaleDateString('vi-VN')}`} />
            <Row label={t('addon.daysCountedLabel')} value={t('addon.daysValue', { days: q.days })} />
            {q.lines.filter((l) => l.extra > 0).map((l) => (
              <Row key={l.key} label={`+${l.extra} ${l.label.toLowerCase()}`} value={t('addon.perMonth', { amount: formatVnd(l.monthly) })} />
            ))}
            <div className="space-y-1.5 border-t pt-3">
              <Row label={t('addon.subtotalLabel', { days: q.days })} value={formatVnd(q.subtotal)} />
              {q.discount > 0 && (
                <Row
                  label={<span className="flex items-center gap-1"><BadgePercent className="h-3.5 w-3.5 text-orange-500" /> {t('addon.currentPeriodDiscount', { percent: q.discountPercent })}</span>}
                  value={<span className="text-orange-600 dark:text-orange-400">−{formatVnd(q.discount)}</span>}
                />
              )}
              {q.promo && (
                <Row
                  label={<span className="flex items-center gap-1"><TicketPercent className="h-3.5 w-3.5 text-primary" /> {t('addon.promoCodeLabel', { code: q.promo.code })}</span>}
                  value={<span className="text-success">−{formatVnd(q.promo.amount)}</span>}
                />
              )}
              <VatRows q={q} />
            </div>
          </div>
        ) : quote.isError ? null : (
          <p className="text-sm text-muted-foreground">{t('addon.calculatingPrice')}</p>
        )}

        <PromoCodeBox
          applied={promoCode}
          promo={q?.promo ?? null}
          note={q?.promoNote ?? null}
          error={q?.promoError ?? null}
          checking={!!promoCode && stale}
          onApply={onPromoChange}
          disabled={busy}
        />

        <div className="flex items-baseline justify-between gap-3 border-t pt-4">
          <span className="text-base font-medium">{t('addon.totalPayment')}</span>
          <span className={cn('text-2xl font-semibold tabular-nums text-primary transition-opacity', stale && 'opacity-50')}>
            {q ? formatVnd(q.total) : '—'}
          </span>
        </div>
        {q?.vat && q.vat.amount > 0 && <p className="-mt-2 text-right text-xs text-muted-foreground">{t('addon.vatIncluded', { vat: vatLabel(q.vat.percent) })}</p>}
        <PayPalChargeNote method={method} totalVnd={q?.total} />
        {increased && quote.isError && <p className="text-sm text-destructive">{apiError(quote.error)}</p>}
        <Row label={t('addon.vatInvoiceLabel')} value={<span className="block max-w-[200px] truncate" title={invoiceLabel}>{invoiceLabel}</span>} />
        {q?.addon && (
          <p className="text-xs text-muted-foreground">
            {t('addon.addonNote', { delta: addonDeltaText(q.addon.delta), days: q.days })}
          </p>
        )}

        <Button
          size="lg"
          className="w-full"
          onClick={pay}
          disabled={!canPay || !q || stale || busy || (!method && !freeOrder) || quote.isError || promoBlocked}
        >
          {busy && <Loader2 className="animate-spin" />}
          {redirecting ? t('addon.redirecting') : freeOrder ? t('addon.activateNow') : t('addon.placeOrder')}
        </Button>
        {!canPay && <p className="text-center text-xs text-muted-foreground">{t('addon.onlyOwnerAdmin')}</p>}
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
          {q?.vat ? `${vatListNote(q.vat.percent)} ` : ''}
          {t('addon.pricingDisclaimer')}
        </p>
      </div>
    </aside>
  )
}
